import { useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Profile() {
  const { user } = useAuth();
  const p = user?.profile || {};
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password updated successfully");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="profile-page">
      <div>
        <p className="section-label">Account</p>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight mt-1">Profile</h2>
      </div>

      <div className="card-surface p-6">
        <h3 className="font-display font-semibold text-white mb-5">Company Details</h3>
        <div className="grid sm:grid-cols-2 gap-5 text-sm">
          {[["Company", p.company], ["Client Code", p.client_code], ["Contact Person", p.contact_person],
            ["Email", p.email], ["Phone", p.phone], ["Industry", p.industry]].map(([label, v]) => (
            <div key={label}>
              <p className="text-[10px] tracking-widest text-slate-500 uppercase">{label}</p>
              <p className="text-white mt-1">{v || "—"}</p>
            </div>
          ))}
        </div>
        {p.description && <p className="text-sm text-slate-400 mt-5 pt-5 border-t border-white/5">{p.description}</p>}
      </div>

      <form onSubmit={submit} className="card-surface p-6" data-testid="change-password-form">
        <h3 className="font-display font-semibold text-white mb-1 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-cyan-400" /> Change Password
        </h3>
        <p className="text-xs text-slate-500 mb-5">Passwords are stored as secure bcrypt hashes.</p>
        <div className="space-y-4 max-w-sm">
          <div>
            <Label className="text-slate-300">Current Password</Label>
            <Input type="password" required value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })}
              className="mt-1.5 bg-secondary/50 border-white/10" data-testid="current-password" />
          </div>
          <div>
            <Label className="text-slate-300">New Password</Label>
            <Input type="password" required minLength={6} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              className="mt-1.5 bg-secondary/50 border-white/10" data-testid="new-password" />
          </div>
          <div>
            <Label className="text-slate-300">Confirm New Password</Label>
            <Input type="password" required minLength={6} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              className="mt-1.5 bg-secondary/50 border-white/10" data-testid="confirm-password" />
          </div>
          <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-500" data-testid="change-password-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update Password"}
          </Button>
        </div>
      </form>
    </div>
  );
}
